/**
 * Come reagire all'interruzione del flusso di un'operazione.
 *
 * Un deploy non vive nella pagina: vive nel server, che ne conserva gli eventi
 * anche se nessuno li sta leggendo. Perdere il collegamento quindi non significa
 * che sia andato storto — significa soltanto che non lo stiamo più guardando.
 *
 * Trattare le due cose come una sola è ciò che porta a dichiarare fallito un
 * deploy riuscito, che è il modo più efficace di far dubitare di uno strumento.
 */

export type Reazione =
  /** L'esito è già noto, o l'interruzione era voluta: niente da segnalare. */
  | { readonly tipo: "ignora" }
  /** L'operazione prosegue nel server: si torna a seguirla. */
  | { readonly tipo: "riaggancia" }
  /** Non è mai partita: il messaggio va mostrato. */
  | { readonly tipo: "fallimento"; readonly messaggio: string };

export interface Interruzione {
  /** Se è già arrivato un evento conclusivo (riuscito o fallito). */
  readonly esitoNoto: boolean;
  readonly eventiRicevuti: number;
  readonly errore: unknown;
}

/** Un annullamento richiesto da noi, tipicamente lasciando la pagina. */
const voluta = (errore: unknown): boolean =>
  errore instanceof Error && (errore.name === "AbortError" || errore.name === "TimeoutError");

export function reazioneA({ esitoNoto, eventiRicevuti, errore }: Interruzione): Reazione {
  if (esitoNoto || voluta(errore)) return { tipo: "ignora" };

  // Senza un solo evento non c'era alcun flusso: la richiesta è stata rifiutata
  // prima di cominciare, e il motivo è quello che il server ha risposto.
  if (eventiRicevuti === 0) {
    return {
      tipo: "fallimento",
      messaggio: errore instanceof Error ? errore.message : String(errore),
    };
  }

  return { tipo: "riaggancia" };
}

/**
 * Le righe di un'operazione, e le fasi che le contengono.
 *
 * I log e le fasi arrivano come due flussi separati — il server li tiene
 * distinti apposta, perché le frasi cambiano e le fasi no — ma chi guarda ne
 * vede una cosa sola: «a che punto è, e cosa sta dicendo adesso». Riunirli qui
 * significa che la console può mostrare un elenco di fasi invece di un muro di
 * righe, e dire di ciascuna quanto è costata.
 */

export interface Riga {
  readonly stream: "stdout" | "stderr" | "portman";
  readonly message: string;
  /** La fase in corso quando è arrivata; 0 prima che ne sia annunciata una. */
  readonly fase: number;
}

/** Una fase attraversata, con il tempo che è costata. */
export interface FaseSvolta {
  readonly titolo: string;
  readonly indice: number;
  readonly totale: number;
  readonly inizio: number;
  /** `null` finché la fase è quella in corso. */
  readonly fine: number | null;
}

export interface Gruppo {
  /** `null` per le righe precedenti alla prima fase, e per chi non ne ha. */
  readonly fase: FaseSvolta | null;
  readonly righe: readonly Riga[];
}

/**
 * Distribuisce le righe fra le fasi, in ordine di svolgimento.
 *
 * Una fase senza righe resta nell'elenco: «Spostamento del traffico» non dice
 * nulla perché non ha nulla da dire, e toglierla farebbe sparire un passo che
 * è avvenuto davvero.
 */
export function raggruppaPerFase(
  righe: readonly Riga[],
  fasi: readonly FaseSvolta[],
): Gruppo[] {
  const perFase = new Map<number, Riga[]>();

  for (const riga of righe) {
    const elenco = perFase.get(riga.fase);

    if (elenco) elenco.push(riga);
    else perFase.set(riga.fase, [riga]);
  }

  const gruppi: Gruppo[] = [];
  const preludio = perFase.get(0);

  if (preludio) gruppi.push({ fase: null, righe: preludio });
  for (const fase of fasi) gruppi.push({ fase, righe: perFase.get(fase.indice) ?? [] });

  return gruppi;
}

/**
 * Una durata come la si legge a voce.
 *
 * I millisecondi sotto il secondo, il decimo fino al minuto, poi minuti e
 * secondi: un pull di sei minuti scritto «374.2s» costringe a fare una
 * divisione per capire se è tanto.
 */
export function formattaDurata(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`;

  const secondi = ms / 1000;
  if (secondi < 60) return `${secondi.toFixed(1)}s`;

  return `${Math.floor(secondi / 60)}m ${String(Math.floor(secondi % 60)).padStart(2, "0")}s`;
}
